/**
 * Firebase auth: email/password, Google, GitHub, forgot password, reset password.
 */
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  confirmPasswordReset,
  applyActionCode,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type User,
} from "firebase/auth";
import { auth, googleProvider, githubProvider } from "../firebase/firebase";
import { getApiBaseUrl } from "./backendApi";

type AuthResult = {
  user: User;
};

type AuthCodeError = Error & {
  code?: string;
};

const createCodeError = (message: string, code?: string): AuthCodeError => {
  const err = new Error(message) as AuthCodeError;
  if (code) {
    err.code = code;
  }
  return err;
};

const getAuthEmailFlags = () => {
  const forceCustomAuthEmails =
    typeof process !== "undefined" &&
    String(process.env.REACT_APP_FORCE_CUSTOM_AUTH_EMAILS || "false").toLowerCase() ===
      "true";
  const allowFirebaseFallback =
    typeof process !== "undefined" &&
    String(process.env.REACT_APP_ALLOW_FIREBASE_AUTH_FALLBACK || "false").toLowerCase() ===
      "true";

  return { forceCustomAuthEmails, allowFirebaseFallback };
};

/**
 * Email/password login.
 * @param rememberMe - If true, local persistence; if false, session only (signed out when tab closes).
 */
export const login = async (
  email: string,
  password: string,
  rememberMe = true
): Promise<AuthResult> => {
  const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
  await setPersistence(auth, persistence);
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return { user: userCredential.user };
};

/**
 * Email/password sign-up. Uses local persistence by default.
 */
export const signup = async (email: string, password: string): Promise<AuthResult> => {
  await setPersistence(auth, browserLocalPersistence);
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  return { user: userCredential.user };
};

/**
 * Google sign-in (popup).
 */
export const loginWithGoogle = async (): Promise<AuthResult> => {
  await setPersistence(auth, browserLocalPersistence);
  const userCredential = await signInWithPopup(auth, googleProvider);
  return { user: userCredential.user };
};

/**
 * GitHub sign-in (popup).
 */
export const loginWithGithub = async (): Promise<AuthResult> => {
  await setPersistence(auth, browserLocalPersistence);
  const userCredential = await signInWithPopup(auth, githubProvider);
  return { user: userCredential.user };
};

/**
 * Send password reset email via the Node API (custom HTML template).
 * Uses the same API base as `apiRequest` (`getApiBaseUrl` from REACT_APP_API_URL at build time).
 * On network failure only, may fall back to Firebase's default email when REACT_APP_ALLOW_FIREBASE_AUTH_FALLBACK=true.
 */
export const sendPasswordReset = async (email: string): Promise<Record<string, never>> => {
  // Same base URL as the rest of the app (backendApi). Set REACT_APP_API_URL at build time if API is on another host.
  const { forceCustomAuthEmails, allowFirebaseFallback } = getAuthEmailFlags();
  const base = getApiBaseUrl();
  const actionCodeSettings = {
    url:
      typeof window !== "undefined"
        ? `${window.location.origin}/reset-password`
        : "http://localhost/reset-password",
    handleCodeInApp: true,
  };

  try {
    const res = await fetch(`${base}/api/auth/send-password-reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data: { error?: string; detail?: string } = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 404) {
        throw createCodeError(data.error || "Failed to send reset email", "auth/user-not-found");
      }
      if (res.status === 400) {
        throw createCodeError(data.error || "Failed to send reset email", "auth/invalid-email");
      }
      const msg =
        data.error ||
        data.detail ||
        (res.status >= 500 ? `Server error (${res.status}). Check API logs and email (SMTP) configuration.` : `Request failed (${res.status})`);
      throw createCodeError(msg);
    }
    return {};
  } catch (err) {
    if (forceCustomAuthEmails || !allowFirebaseFallback) {
      throw err;
    }
    const isNetworkFailure =
      err instanceof TypeError ||
      (err instanceof Error && /failed to fetch|networkerror|load failed/i.test(err.message));
    if (!isNetworkFailure) {
      throw err;
    }
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
    return {};
  }
};

/**
 * Complete password reset using the oobCode from the email link.
 * Call from /reset-password page after user enters new password.
 */
export const resetPasswordWithCode = async (
  oobCode: string,
  newPassword: string
): Promise<Record<string, never>> => {
  await confirmPasswordReset(auth, oobCode, newPassword);
  return {};
};

/**
 * Send email verification link to the current user, pointing to our custom verify-email page.
 */
export const sendVerificationEmail = async (user: User): Promise<void> => {
  if (!user) throw new Error("No user to verify");
  if (!user.email) throw new Error("User has no email");

  const { forceCustomAuthEmails, allowFirebaseFallback } = getAuthEmailFlags();
  const base = getApiBaseUrl();
  const actionCodeSettings = {
    url:
      typeof window !== "undefined"
        ? `${window.location.origin}/verify-email`
        : "http://localhost/verify-email",
    handleCodeInApp: true,
  };

  const res = await fetch(`${base}/api/auth/send-verification-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: user.email }),
  });
  const data: { error?: string } = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (forceCustomAuthEmails || !allowFirebaseFallback) {
      throw new Error(data.error || "Failed to send verification email via custom API");
    }
    // If custom mailer fails, fallback to Firebase default template.
    await sendEmailVerification(user, actionCodeSettings);
  }
};

/**
 * Apply an email verification action code from the verification link.
 * Used on /verify-email page.
 */
export const verifyEmailWithCode = async (
  oobCode: string
): Promise<Record<string, never>> => {
  await applyActionCode(auth, oobCode);
  if (auth.currentUser) {
    await auth.currentUser.reload();
  }
  return {};
};
